/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Editor, EditorContent, JSONContent, useEditor } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Mention from '@tiptap/extension-mention';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Placeholder from '@tiptap/extension-placeholder';
import createSuggestion from './suggestion.ts';
import { WorkspaceMemberWithUser } from '@/common/types/databaseTypes.js';
import { Box } from '@mantine/core';
import { createStyles } from '@mantine/styles';
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react';
import { convertPlainTextToTiptapHtml } from '../utils/mentionUtils.ts';
import { useTranslation } from 'react-i18next';

export interface MentionableEditorRef {
    clearContent: () => void;
}

const useStyles = createStyles(theme => ({
    editorWrapper: {
        flex: 1,
        backgroundColor: theme.colors.gray[1],
        padding: '4px 12px',
        borderRadius: '8px',

        // 전역 대신 중첩 선택자 사용
        '& .ProseMirror': {
            outline: 'none',
            padding: 0,
        },
        '& .ProseMirror p': {
            margin: '2px auto',
        },
        '& .ProseMirror p.is-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            color: '#adb5bd',
            pointerEvents: 'none',
            height: 0,
            position: 'absolute',
            zIndex: 0,
        },
    },
}));

const MentionableEditor = forwardRef<
    MentionableEditorRef,
    {
        mentionableUsers: WorkspaceMemberWithUser[];
        loadingUsers: boolean;
        initialContent?: string; // 편집 모드일 경우
        onChange?: (
            content: string,
            mentions: { id: string; label: string }[],
            json: JSONContent
        ) => void;
        autoFocus?: boolean;
        mentionable?: boolean;
    }
>((props, ref) => {
    const {
        mentionableUsers,
        loadingUsers,
        initialContent, // 편집 모드일 경우
        onChange,
        autoFocus,
        mentionable = true, // 멘션 사용하고 싶지 않을 때 false 전달
    } = props;
    const { classes } = useStyles();
    const { t } = useTranslation('comment');

    const suggestion = useMemo(() => {
        if (!mentionable) return null;

        return createSuggestion({
            userList: mentionableUsers.map(member => member.user),
        }) as any;
    }, [mentionableUsers, mentionable]);

    const editorHadFocusRef = useRef(false);
    const internalEditorRef = useRef(null);

    // 사용자 맵 생성 (id를 키로 사용)
    const userMap = useMemo(() => {
        if (!mentionable) return null;

        const map: Record<string, { id: string; name: string }> = {};
        mentionableUsers.forEach(member => {
            map[member.user.id] = {
                id: member.user.id,
                name: member.user.name,
            };
        });
        return map;
    }, [mentionableUsers, mentionable]);

    // 초기 콘텐츠가 있으면 HTML로 변환
    const initialHtml = useMemo(() => {
        if (!initialContent || loadingUsers) return '';
        //  || Object.keys(userMap).length === 0) return '';

        return convertPlainTextToTiptapHtml(initialContent, userMap);
    }, [initialContent, loadingUsers, userMap]);

    // 에디터 내용에서 멘션 정보 추출하기
    const getMentionedUsers = (
        editor: Editor
    ): { id: string; label: string }[] => {
        const mentions: { id: string; label: string }[] = [];

        // 모든 멘션 노드를 찾음
        editor.state.doc.descendants(node => {
            if (node.type.name === 'mention') {
                mentions.push({
                    id: node.attrs.id,
                    label: node.attrs.label || node.attrs.id,
                });
            }
        });

        return mentions;
    };

    const editor = useEditor(
        {
            extensions: [
                Document.configure({
                    // 마지막 빈 단락 추가 방지
                    trailing: false,
                }),
                Paragraph,
                Text,
                Placeholder.configure({
                    placeholder: t('comment.placeholder'),
                    showOnlyCurrent: true, // 현재 노드에만 표시
                    includeChildren: false, // 자식 노드에는 적용하지 않음
                }),
                mentionable
                    ? Mention.configure({
                          HTMLAttributes: {
                              class: 'mention',
                              style: 'color: #228BE6; font-weight: 700;',
                          },
                          suggestion,
                      })
                    : undefined,
            ],
            onUpdate: ({ editor }) => {
                if (onChange) {
                    // 에디터 내용과 함께 멘션 정보도 전달
                    const mentions = mentionable
                        ? getMentionedUsers(editor)
                        : [];
                    onChange(editor.getHTML(), mentions, editor.getJSON());
                }
            },
            onFocus: () => {
                editorHadFocusRef.current = true;
            },
            onBlur: () => {
                // 잠시 지연 후 포커스 상태 업데이트 (재렌더링시 즉시 업데이트되는 것 방지)
                setTimeout(() => {
                    // 다른 에디터 관련 요소로 포커스가 이동하는 경우 체크
                    if (document.activeElement !== internalEditorRef.current) {
                        editorHadFocusRef.current = false;
                    }
                }, 100);
            },
            editorProps: {
                attributes: {
                    style: 'font-size: 14px;',
                },
            },
            editable: true,
            content: initialHtml,
            autofocus: autoFocus || false,
        },
        [mentionableUsers] // 멤버 불러온 뒤 다시 그려주기
    );

    // 에디터가 로드되고 autofocus가 true일 때 커서를 끝으로 이동 또는, mentionableUsers 변경에 따른 포커스 복원
    useEffect(() => {
        if (editor && (autoFocus || editorHadFocusRef.current)) {
            setTimeout(() => {
                editor.commands.focus('end');
            }, 0);
        }
    }, [editor, mentionableUsers, autoFocus]);

    const clearContent = useCallback(() => {
        if (editor) {
            editor.commands.clearContent();
        }
    }, [editor]);

    // ref를 통해 함수 노출
    useImperativeHandle(ref, () => ({
        clearContent,
    }));

    if (!editor) {
        return null;
    }

    return (
        <Box flex={1} className={classes.editorWrapper}>
            <EditorContent editor={editor} />
        </Box>
    );
});

MentionableEditor.displayName = 'MentionableEditor';

export default MentionableEditor;
